import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeditationTimer = () => {
  const { isAdminMode, token } = useAuth();
  // FORCE REBUILD TRIGGER - Meditation Timer Component
  const [duration, setDuration] = useState(1200); // 20 minutes in seconds
  const [timeLeft, setTimeLeft] = useState(1200);
  const [isRunning, setIsRunning] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.5);
  const [availableAudioFiles, setAvailableAudioFiles] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Load available audio files on component mount
  useEffect(() => {
    console.log('🔄 Component mounted - fetching audio files');
    console.log('- User mode:', isAdminMode ? 'Admin' : 'Regular User');
    console.log('- Token exists:', !!token);
    fetchAudioFiles();
  }, []);

  const fetchAudioFiles = async () => {
    try {
      console.log('🔄 Fetching audio files...');
      console.log('- API endpoint:', `${API}/meditation-timer`);
      
      // Don't require authentication for fetching audio files
      const response = await axios.get(`${API}/meditation-timer`, {
        timeout: 10000 // 10 second timeout
      });
      
      console.log('📥 Raw API response:', response);
      console.log('📥 Audio files response data:', response.data);
      
      const { audio_files, default_audio } = response.data;
      console.log('📥 Parsed audio_files:', audio_files);
      console.log('📥 Parsed default_audio:', default_audio);
      
      setAvailableAudioFiles(audio_files || []);
      
      // Auto-enable audio if any files are available
      if (audio_files && audio_files.length > 0) {
        console.log('✅ Audio files found:', audio_files.length);
        
        // Find default audio or use first available
        const defaultAudioFile = audio_files.find(audio => audio.is_default) || audio_files[0];
        console.log('✅ Selected audio file:', defaultAudioFile);
        
        if (defaultAudioFile) {
          // Handle absolute URLs (Cloudinary) vs relative URLs (local)
          const audioUrl = defaultAudioFile.url.startsWith('http') 
            ? defaultAudioFile.url  // Already absolute URL (Cloudinary)
            : `${BACKEND_URL}${defaultAudioFile.url}`;  // Relative URL (local files)
          console.log('✅ Audio URL constructed:', audioUrl);
          
          setSelectedAudioId(defaultAudioFile.id);
          setAudioFile(audioUrl);
          setIsAudioEnabled(true); // Auto-enable audio
          
          console.log('✅ Audio auto-enabled for all users:', {
            filename: defaultAudioFile.original_filename,
            id: defaultAudioFile.id,
            url: audioUrl,
            enabled: true
          });
        }
      } else {
        console.log('ℹ️ No audio files available');
      }
    } catch (error) {
      console.error('❌ Error fetching audio files:', error);
      if (error.response) {
        console.error('Response error status:', error.response.status);
        console.error('Response error data:', error.response.data);
      } else if (error.request) {
        console.error('Network error - no response received');
      } else {
        console.error('Request setup error:', error.message);
      }
    }
  };

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsCompleted(true);
            // Stop audio when timer completes
            if (audioRef.current) {
              audioRef.current.pause();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  // Audio control effects
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = audioVolume;
      audioRef.current.loop = true; // Loop the Om sound during meditation
      audioRef.current.preload = 'auto'; // Preload audio
      audioRef.current.muted = false; // Ensure not muted
      
      // Allow full audio playback including TING sounds at beginning and end
      audioRef.current.addEventListener('ended', () => {
        console.log('🔔 Audio ended naturally with TING sound - restarting with full cycle');
        // Let the audio complete its full cycle including TING sounds
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => console.error('Audio restart failed:', e));
          }
        }, 100); // Small delay to ensure clean restart
      });
      
      console.log('🎵 Audio settings updated:');
      console.log('- Volume:', audioVolume);
      console.log('- Loop:', audioRef.current.loop);
      console.log('- Preload:', audioRef.current.preload);
      console.log('- Muted:', audioRef.current.muted);
      console.log('- Full TING cycle preserved');
    }
  }, [audioVolume, audioFile]);

  // Force audio element initialization when audioFile changes
  useEffect(() => {
    console.log('🎯 Audio file state changed:', audioFile);
    console.log('🎯 Audio ref current:', !!audioRef.current);
    
    if (audioFile) {
      // Wait for next render cycle to ensure audio element is in DOM
      const timer = setTimeout(() => {
        if (audioRef.current) {
          console.log('🎵 Audio element initialized:', audioRef.current.src);
          audioRef.current.volume = audioVolume;
          audioRef.current.load(); // Force load the new audio file
        } else {
          console.log('⚠️ Audio ref still not ready after audioFile change');
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [audioFile]);

  // Debug useEffect to check when audio ref becomes available
  useEffect(() => {
    const checkAudioRef = () => {
      console.log('🔍 Audio ref status check:', !!audioRef.current);
      if (audioRef.current) {
        console.log('✅ Audio element successfully mounted and connected to ref');
        console.log('- Audio element src:', audioRef.current.src);
        console.log('- Audio element volume:', audioRef.current.volume);
        console.log('- Audio element ready state:', audioRef.current.readyState);
      }
    };
    
    // Check immediately and after a short delay
    checkAudioRef();
    const timer = setTimeout(checkAudioRef, 500);
    
    return () => clearTimeout(timer);
  }, []); // Only run on mount

  const handleCategoryUpload = async (event, category, categoryLabel) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['wav', 'mp3', 'ogg'];
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      alert('Please upload a valid audio file (WAV, MP3, or OGG format)');
      return;
    }

    // Check file size (limit to 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size too large. Please upload a file smaller than 50MB.');
      return;
    }

    setUploadingAudio(true);
    
    try {
      // First, delete any existing file for this category
      const existingFile = availableAudioFiles.find(audio => audio.category === category);
      if (existingFile) {
        await axios.delete(`${API}/meditation-timer/audio/${existingFile.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Upload new file
      const formData = new FormData();
      formData.append('audio_file', file);
      formData.append('category', category);
      formData.append('display_name', categoryLabel);
      formData.append('description', `${categoryLabel} meditation audio`);
      
      const response = await axios.post(`${API}/meditation-timer/upload-audio`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      
      if (response.data.success) {
        console.log('✅ Audio uploaded:', categoryLabel);
        await fetchAudioFiles(); // Refresh list
        event.target.value = ''; // Clear input
      }
    } catch (error) {
      console.error('❌ Upload failed:', error);
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('Uploading file:', file.name, 'Type:', file.type, 'Size:', file.size);
    
    // Validate file type
    const validTypes = ['audio/wav', 'audio/wave', 'audio/x-wav', 'audio/mp3', 'audio/mpeg', 'audio/ogg'];
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const validExtensions = ['wav', 'mp3', 'ogg'];
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      alert('Please upload a valid audio file (WAV, MP3, or OGG format)');
      return;
    }

    // Check file size (limit to 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size too large. Please upload a file smaller than 50MB.');
      return;
    }

    // Get category and metadata from user input
    const category = document.getElementById('audio-category')?.value || 'om_chanting';
    const displayName = document.getElementById('display-name')?.value || '';
    const description = document.getElementById('description')?.value || '';

    setUploadingAudio(true);
    
    try {
      const formData = new FormData();
      formData.append('audio_file', file);
      formData.append('audio_name', file.name);
      formData.append('category', category);
      formData.append('display_name', displayName);
      formData.append('description', description);
      formData.append('is_default', 'false'); // Let users choose their preferred default
      
      console.log('📤 Starting upload to:', `${API}/meditation-timer/upload-audio`);
      console.log('📤 Category:', category, 'Display name:', displayName);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000); // 60 second timeout
      
      const response = await axios.post(`${API}/meditation-timer/upload-audio`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        signal: controller.signal,
        timeout: 60000, // 60 second timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📤 Upload progress: ${percentCompleted}%`);
        }
      });
      
      clearTimeout(timeoutId);
      
      if (response.data.success) {
        console.log('✅ Audio file uploaded successfully');
        alert(`🎵 ${displayName || file.name} uploaded successfully!`);
        
        // Refresh audio files list FIRST
        console.log('🔄 Refreshing audio files after upload...');
        await fetchAudioFiles();
        
        // Get the uploaded file from the response and set it immediately
        if (response.data.audio) {
          const newAudio = response.data.audio;
          // Handle absolute URLs (Cloudinary) vs relative URLs (local)
          const newAudioUrl = newAudio.url.startsWith('http') 
            ? newAudio.url  // Already absolute URL (Cloudinary)
            : `${BACKEND_URL}${newAudio.url}`;  // Relative URL (local files)
          
          console.log('✅ Setting newly uploaded audio as active:', newAudioUrl);
          setSelectedAudioId(newAudio.id);
          setAudioFile(newAudioUrl);
          setIsAudioEnabled(true);
          
          // Update available files list to include new file
          setAvailableAudioFiles(prev => {
            // Remove any existing file with same ID and add the new one
            const filtered = prev.filter(audio => audio.id !== newAudio.id);
            return [...filtered, newAudio];
          });
        }
        
        // Clear the form
        event.target.value = '';
        document.getElementById('display-name').value = '';
        document.getElementById('description').value = '';
      } else {
        console.error('❌ Upload failed:', response.data);
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ Audio upload failed:', error);
      
      if (error.name === 'AbortError') {
        alert('Upload timed out. Please try again with a smaller file.');
      } else if (error.response) {
        console.error('Response error:', error.response.data);
        alert(`Upload failed: ${error.response.data.detail || 'Server error'}`);
      } else if (error.request) {
        console.error('Network error:', error.request);
        alert('Network error. Please check your connection and try again.');
      } else {
        alert('Upload failed. Please try again.');
      }
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleAudioSelection = (audioId) => {
    const selectedAudio = availableAudioFiles.find(audio => audio.id === audioId);
    if (selectedAudio) {
      setSelectedAudioId(audioId);
      // Handle absolute URLs (Cloudinary) vs relative URLs (local)
      const audioUrl = selectedAudio.url.startsWith('http') 
        ? selectedAudio.url  // Already absolute URL (Cloudinary)
        : `${BACKEND_URL}${selectedAudio.url}`;  // Relative URL (local files)
      setAudioFile(audioUrl);
      setIsAudioEnabled(true);
      console.log('🎵 Audio selected:', selectedAudio.display_name || selectedAudio.original_filename);
    }
  };

  const resumeAudio = async () => {
    console.log('🎵 Resuming audio playback...');
    console.log('- Audio file exists:', !!audioFile);
    console.log('- Audio ref exists:', !!audioRef.current);
    
    if (!audioFile) {
      console.log('❌ No audio file available');
      return;
    }

    if (audioRef.current) {
      try {
        // Resume audio from where it was paused (don't reset currentTime)
        console.log('▶️ Resuming audio from position:', audioRef.current.currentTime);
        audioRef.current.volume = audioVolume;
        audioRef.current.muted = false;
        
        await audioRef.current.play();
        console.log('✅ Audio resumed successfully');
      } catch (error) {
        console.error('❌ Audio resume failed:', error);
      }
    } else {
      console.log('❌ Audio ref not available for resume');
    }
  };

  const startAudio = () => {
    console.log('🎵 Starting audio playback...');
    console.log('- Audio file exists:', !!audioFile);
    console.log('- Audio ref exists:', !!audioRef.current);
    
    if (!audioFile) {
      console.log('❌ No audio file available');
      return;
    }

    // If audio ref doesn't exist, wait for it to be created (max 3 retries)
    const maxRetries = 3;
    let retryCount = 0;
    
    const attemptPlay = () => {
      if (audioRef.current) {
        console.log('✅ Audio ref found, attempting playback');
        console.log('Audio file:', audioFile);
        console.log('Audio volume:', audioVolume);
        
        // Ensure audio is loaded and ready
        audioRef.current.load(); // Force reload the audio
        audioRef.current.currentTime = 0; // Reset to beginning
        audioRef.current.volume = audioVolume; // Ensure volume is set
        audioRef.current.muted = false; // Ensure not muted
        
        // Add event listeners for debugging
        audioRef.current.addEventListener('loadstart', () => console.log('🎵 Audio loadstart'));
        audioRef.current.addEventListener('loadeddata', () => console.log('🎵 Audio loadeddata'));
        audioRef.current.addEventListener('canplay', () => console.log('🎵 Audio canplay'));
        audioRef.current.addEventListener('canplaythrough', () => console.log('🎵 Audio canplaythrough'));
        audioRef.current.addEventListener('error', (e) => console.error('❌ Audio error:', e));
        
        // Wait a moment for audio to load, then play
        setTimeout(() => {
          const playPromise = audioRef.current.play();
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('✅ Audio started playing successfully');
                console.log('🔊 Volume level:', audioRef.current.volume);
                console.log('🔇 Muted:', audioRef.current.muted);
              })
              .catch(error => {
                console.error('❌ Audio play failed:', error);
                console.error('Error details:', error.message);
                
                // Try to enable audio through user interaction
                console.log('🔄 Attempting audio retry...');
                
                // Try to play again after a short delay
                setTimeout(() => {
                  if (audioRef.current) {
                    audioRef.current.play().catch(e => console.error('Retry audio play failed:', e));
                  }
                }, 500);
              });
          }
        }, 200);
        
      } else if (retryCount < maxRetries) {
        retryCount++;
        console.log(`⏳ Audio ref not ready, retry ${retryCount}/${maxRetries} in 500ms...`);
        setTimeout(attemptPlay, 500);
      } else {
        console.error('❌ Audio ref never became available after', maxRetries, 'retries');
        console.error('This indicates a React rendering issue with the audio element');
      }
    };
    
    attemptPlay();
  };

  const pauseAudio = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      console.log('⏸️ Audio paused at position:', audioRef.current.currentTime);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    const isResume = timeLeft < duration;
    console.log('🎬 Starting timer...');
    console.log('- Audio file exists:', !!audioFile);
    console.log('- Audio file URL:', audioFile);
    console.log('- Audio enabled:', isAudioEnabled);
    console.log('- Is resume:', isResume);
    
    setIsRunning(true);
    setIsCompleted(false);
    
    // Auto-start or resume audio if available
    if (audioFile) {
      if (isResume) {
        console.log('▶️ Resuming audio playback...');
        resumeAudio();
      } else {
        console.log('▶️ Starting fresh audio playback...');
        startAudio();
      }
    } else {
      console.log('❌ No audio file to start');
    }
  };

  const pauseTimer = () => {
    setIsRunning(false);
    console.log('⏸️ Timer paused');
    // Pause audio without resetting position
    pauseAudio();
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(duration);
    setIsCompleted(false);
    // Stop audio when reset
    stopAudio();
  };

  const setCustomDuration = (minutes) => {
    const seconds = minutes * 60;
    setDuration(seconds);
    setTimeLeft(seconds);
    setIsRunning(false);
    setIsCompleted(false);
  };

  const progress = ((duration - timeLeft) / duration) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-teal-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Meditation Timer</h1>
          <p className="text-gray-600">Find your inner peace and center yourself</p>
        </div>

        {/* Timer Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
          {/* Timer Display */}
          <div className="relative mb-8">
            <div className="w-64 h-64 mx-auto relative">
              {/* Progress Circle */}
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="#f3f4f6"
                  strokeWidth="8"
                  fill="transparent"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="#6366f1"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray="282.7"
                  strokeDashoffset={282.7 - (progress * 282.7) / 100}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              
              {/* Time Display */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl font-bold text-gray-800 mb-2">
                    {formatTime(timeLeft)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {isCompleted ? 'Completed!' : isRunning ? 'Meditating...' : 'Ready to begin'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Completion Message */}
          {isCompleted && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl mb-2">🧘‍♀️</div>
              <h3 className="text-lg font-semibold text-green-800 mb-1">Meditation Complete!</h3>
              <p className="text-green-600">You have successfully completed your meditation session.</p>
            </div>
          )}

          {/* Timer Controls */}
          <div className="flex justify-center space-x-4 mb-8">
            {!isRunning ? (
              <button
                onClick={startTimer}
                disabled={timeLeft === 0}
                className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full font-semibold hover:shadow-lg transition-shadow disabled:opacity-50"
              >
                {timeLeft === duration ? 'Start' : 'Resume'}
              </button>
            ) : (
              <button
                onClick={pauseTimer}
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full font-semibold hover:shadow-lg transition-shadow"
              >
                Pause
              </button>
            )}
            
            <button
              onClick={resetTimer}
              className="px-8 py-3 bg-gray-500 text-white rounded-full font-semibold hover:bg-gray-600 transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Duration Presets */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Duration Presets</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[5, 10, 15, 20].map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => setCustomDuration(minutes)}
                  disabled={isRunning}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                    duration === minutes * 60
                      ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {minutes}m
                </button>
              ))}
            </div>
          </div>

          {/* Audio Upload and Controls - ADMIN ONLY */}
          {isAdminMode && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">🕉️ Om Audio Management (Admin Only)</h3>
              <div className="text-xs text-purple-600 mb-4 font-medium">
                🔐 Only administrators can upload meditation audio files
              </div>
              
              {/* Simple 6-Button Audio Upload */}
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">🎵 Upload Meditation Audio (6 Categories)</h4>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'om_chanting', icon: '🕉️', label: 'Om Chanting' },
                    { key: 'ocean_waves', icon: '🌊', label: 'Ocean Waves' },
                    { key: 'babbling_stream', icon: '🏞️', label: 'Stream' },
                    { key: 'summer_storm', icon: '⛈️', label: 'Storm' },
                    { key: 'jungle_sounds', icon: '🌴', label: 'Jungle' },
                    { key: 'cosmic_soundscape', icon: '🌌', label: 'Cosmic' }
                  ].map(category => (
                    <div key={category.key} className="text-center">
                      <label className="cursor-pointer">
                        <div className="p-3 border-2 border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors">
                          <div className="text-2xl mb-1">{category.icon}</div>
                          <div className="text-sm font-medium text-gray-700">{category.label}</div>
                          <div className="text-xs text-gray-500 mt-1">Click to upload</div>
                        </div>
                        <input
                          type="file"
                          accept=".wav,.mp3,.ogg,audio/*"
                          className="hidden"
                          onChange={(e) => handleCategoryUpload(e, category.key, category.label)}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                
                {uploadingAudio && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm text-blue-600 font-medium">Uploading...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Simple Audio Selection */}
              {availableAudioFiles.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Audio File
                  </label>
                  <select
                    value={selectedAudioId || ''}
                    onChange={(e) => handleAudioSelection(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Choose audio...</option>
                    {availableAudioFiles.map(audio => (
                      <option key={audio.id} value={audio.id}>
                        {audio.display_name || audio.original_filename}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Audio Controls */}
              {audioFile && (
                <div className="space-y-3">
                  {/* Volume Control */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Volume: {Math.round(audioVolume * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={audioVolume}
                      onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>

                  {/* Audio Test Button */}
                  <button
                    onClick={() => {
                      if (audioRef.current) {
                        console.log('🎵 Test button clicked');
                        console.log('Audio state:', {
                          paused: audioRef.current.paused,
                          currentTime: audioRef.current.currentTime,
                          volume: audioRef.current.volume,
                          muted: audioRef.current.muted,
                          src: audioRef.current.src,
                          readyState: audioRef.current.readyState
                        });
                        
                        if (audioRef.current.paused) {
                          // Force load and play
                          audioRef.current.load();
                          audioRef.current.volume = audioVolume;
                          audioRef.current.muted = false;
                          
                          const playPromise = audioRef.current.play();
                          if (playPromise !== undefined) {
                            playPromise
                              .then(() => {
                                console.log('✅ Test audio playing successfully');
                              })
                              .catch(error => {
                                console.error('❌ Test audio failed:', error);
                                alert(`Audio test failed: ${error.message}. Try clicking the start button to enable audio.`);
                              });
                          }
                        } else {
                          audioRef.current.pause();
                          audioRef.current.currentTime = 0;
                          console.log('⏸️ Test audio stopped');
                        }
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                  >
                    🎵 Test Audio
                  </button>
                </div>
              )}

              {/* Audio Status Display */}
              <p className="text-xs text-gray-500 mt-2">
                Audio ready: {audioFile ? '✅' : '❌'} | Ref ready: {audioRef.current ? '✅' : '❌'}
              </p>

              {/* User Volume Control - Always show if audio exists */}
              {audioFile && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Audio Volume: {Math.round(audioVolume * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={audioVolume}
                    onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Audio plays automatically with timer
                  </p>
                </div>
              )}
              
              {!audioFile && (
                <p className="text-sm text-gray-600">
                  {isAdminMode 
                    ? "Upload an audio file above to enable meditation sounds for all users." 
                    : "No meditation audio available. Ask an admin to upload audio files."
                  }
                </p>
              )}
            </div>
          )}

          {/* Audio Configuration and Settings */}
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              🎵 Audio Settings
              {audioFile && <span className="ml-2 text-sm text-green-600">✅ Ready</span>}
              {!audioFile && <span className="ml-2 text-sm text-gray-500">❌ No audio</span>}
            </h4>

            {/* Colorful Audio Selection Buttons for Users */}
            {availableAudioFiles.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                  Choose Your Meditation Audio
                </label>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: 'om_chanting', icon: '🕉️', label: 'Om Chanting', gradient: 'from-orange-400 to-amber-600' },
                    { key: 'ocean_waves', icon: '🌊', label: 'Ocean Waves', gradient: 'from-blue-400 to-teal-600' },
                    { key: 'babbling_stream', icon: '🏞️', label: 'Stream', gradient: 'from-green-400 to-emerald-600' },
                    { key: 'summer_storm', icon: '⛈️', label: 'Storm', gradient: 'from-gray-600 to-slate-800' },
                    { key: 'jungle_sounds', icon: '🌴', label: 'Jungle', gradient: 'from-emerald-500 to-green-700' },
                    { key: 'cosmic_soundscape', icon: '🌌', label: 'Cosmic', gradient: 'from-purple-600 to-indigo-800' }
                  ].map(category => {
                    const categoryAudio = availableAudioFiles.find(audio => audio.category === category.key);
                    const isSelected = categoryAudio && selectedAudioId === categoryAudio.id;
                    const hasAudio = !!categoryAudio;
                    
                    return (
                      <button
                        key={category.key}
                        onClick={() => hasAudio && handleAudioSelection(categoryAudio.id)}
                        disabled={!hasAudio}
                        className={`relative p-4 rounded-xl text-white transition-all duration-200 transform hover:scale-105 ${
                          hasAudio 
                            ? `bg-gradient-to-br ${category.gradient} shadow-lg hover:shadow-xl cursor-pointer` 
                            : 'bg-gray-300 cursor-not-allowed opacity-50'
                        } ${isSelected ? 'ring-4 ring-white ring-opacity-60' : ''}`}
                      >
                        <div className="text-center">
                          <div className="text-3xl mb-2">{category.icon}</div>
                          <div className="font-semibold">{category.label}</div>
                          {!hasAudio && (
                            <div className="text-xs opacity-75 mt-1">No audio</div>
                          )}
                          {hasAudio && isSelected && (
                            <div className="text-xs mt-1">✅ Selected</div>
                          )}
                        </div>
                        
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <div className="bg-white rounded-full p-1">
                              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {availableAudioFiles.length === 0 && (
              <div className="mb-6 text-center py-8 bg-white rounded-lg border border-gray-200">
                <div className="text-4xl mb-3">🎵</div>
                <p className="text-gray-600 mb-2">No meditation audio available</p>
                <p className="text-sm text-gray-500">
                  {isAdminMode 
                    ? "Upload audio files using the form above to create your meditation library."
                    : "Ask an admin to upload meditation audio files."
                  }
                </p>
              </div>
            )}
            
            {/* Debug Information */}
            {audioFile && (
              <div className="mb-4 p-3 bg-white rounded-lg border text-xs">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <strong>File:</strong> {availableAudioFiles.find(a => a.id === selectedAudioId)?.original_filename || 'Unknown'}
                  </div>
                  <div>
                    <strong>Volume:</strong> {Math.round(audioVolume * 100)}%
                  </div>
                  <div className="md:col-span-2">
                    <strong>URL:</strong> <span className="break-all">{audioFile}</span>
                  </div>
                  <div>
                    <strong>Audio Element:</strong> {audioRef.current ? '✅ Ready' : '❌ Loading'}
                  </div>
                  <div>
                    <strong>Browser Support:</strong> {navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Firefox') ? 'Firefox' : 'Other'}
                  </div>
                </div>
              </div>
            )}

            {/* User Volume Control - Enhanced */}
            {audioFile && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio Volume: {Math.round(audioVolume * 100)}%
                </label>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500">🔇</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={audioVolume}
                    onChange={(e) => {
                      const newVolume = parseFloat(e.target.value);
                      setAudioVolume(newVolume);
                      // Apply volume immediately if audio element exists
                      if (audioRef.current) {
                        audioRef.current.volume = newVolume;
                      }
                    }}
                    className="flex-1 h-2 bg-gradient-to-r from-gray-200 to-purple-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <span className="text-xs text-gray-500">🔊</span>
                </div>
                
                {/* Audio Test Button */}
                <button
                  onClick={() => {
                    if (audioRef.current && audioFile) {
                      console.log('🔊 Testing audio...');
                      audioRef.current.currentTime = 0;
                      audioRef.current.volume = audioVolume;
                      const playPromise = audioRef.current.play();
                      
                      if (playPromise) {
                        playPromise
                          .then(() => {
                            console.log('✅ Audio test successful');
                            // Stop after 2 seconds
                            setTimeout(() => audioRef.current.pause(), 2000);
                          })
                          .catch(err => {
                            console.error('❌ Audio test failed:', err);
                            alert('Audio test failed. This may be due to browser autoplay restrictions. Audio will work when you start the timer.');
                          });
                      }
                    }
                  }}
                  className="mt-2 px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  🎵 Test Audio (2 seconds)
                </button>
                
                <p className="text-xs text-gray-500 mt-2">
                  💡 <strong>Note:</strong> Audio plays automatically when you start the meditation timer. 
                  If you hear no sound, try the test button above - some browsers require user interaction first.
                </p>
              </div>
            )}
          </div>

          {/* Single Global Audio Element - Always render when audio is selected */}
          <audio
            ref={audioRef}
            src={audioFile || ""}
            preload="auto"
            loop={true}
            volume={audioVolume}
            crossOrigin="anonymous"
            controls={false}
            style={{ display: 'none' }}
            onLoadStart={() => console.log('🎵 Audio load started')}
            onLoadedData={() => console.log('🎵 Audio data loaded')}
            onCanPlay={() => {
              console.log('🎵 Audio can play');
              console.log('🎵 Audio ref ready:', !!audioRef.current);
            }}
            onCanPlayThrough={() => console.log('🎵 Audio can play through')}
            onError={(e) => console.error('❌ Audio element error:', e)}
            onPlay={() => console.log('▶️ Audio started')}
            onPause={() => console.log('⏸️ Audio paused')}
            onEnded={() => console.log('🔚 Audio ended')}
            onVolumeChange={() => console.log('🔊 Volume changed:', audioRef.current?.volume)}
          />

          {/* Audio Notice */}
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3 mb-6">
            <p>🔊 <strong>Audio:</strong> {isAdminMode ? 'Upload and manage Om chanting or meditation music. Files are saved permanently and available to all users.' : audioFile ? 'Meditation audio will play automatically when you start the timer.' : 'No meditation audio available.'}</p>
            {audioFile && (
              <div className="mt-2 text-xs text-gray-400">
                <p>Current audio: {availableAudioFiles.find(a => a.id === selectedAudioId)?.original_filename || 'Audio file loaded'}</p>
                <p>Volume: {Math.round(audioVolume * 100)}%</p>
              </div>
            )}
          </div>
        </div>

        {/* Meditation Tips */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Meditation Tips</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="space-y-2">
              <p>• Find a quiet, comfortable space</p>
              <p>• Sit with your spine straight</p>
              <p>• Focus on your breathing</p>
            </div>
            <div className="space-y-2">
              <p>• Let thoughts pass without judgment</p>
              <p>• Start with shorter sessions</p>
              <p>• Be consistent with your practice</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeditationTimer;